import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { useSelector } from "react-redux";
import { Eye, RefreshCw, RotateCw, Star } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { getCustomerFeedback, retryCustomerFeedbackPush } from "../../utils/customerFeedbackApi";
import { successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader as RLoader, TablePagination } from "../reporting/ReportingShared";
import FeedbackDetailModal from "./FeedbackDetailModal";

const iconBtn = {
  background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12,
  color: C.textMed, padding: "5px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};
const coralBtn = {
  background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13,
  color: C.white, padding: "8px 16px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
};
const field = {
  padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13,
  outline: "none", fontFamily: "inherit", color: C.text, background: C.white,
};

// Mirrors the CustomerFeedback.merchant_push_status enum. `stale` is the one that matters:
// it means Odoo never got the order this feedback belongs to, and only a human can decide
// whether it is worth re-driving.
const STATUS_META = {
  pending: { label: "Waiting", color: C.orange, bg: C.orangeLight },
  success: { label: "Synced", color: "#0F7B6C", bg: C.coralLight },
  stale: { label: "Stale", color: "#B42318", bg: "#FEE4E2" },
  failed: { label: "Failed", color: "#B42318", bg: "#FEE4E2" },
};

const PAGE_SIZE = 25;

const EMPTY_FILTERS = { pushStatus: "", rating: "", branchId: "", from: "", to: "" };

/**
 * Receipt feedback — the 1-5 star ratings customers leave by scanning the QR on a POS
 * receipt.
 *
 * This screen exists for the `stale` pile rather than for browsing. Odoo only creates a
 * review once that order's uid has reached it, so feedback left before the order synced is
 * retried on a ladder and, if the order never arrives, ends up `stale` awaiting a human.
 * Filtering to those and re-driving them in bulk is the workflow; the rest is context.
 */
const CustomerFeedbackList = () => {
  const [page, setPage] = useState({ rows: [], total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [detail, setDetail] = useState(null);

  // Standard merchant-scoping idiom: a merchant user is pinned to its own merchant, an
  // admin picks one from the header selector.
  const userType = localStorage.getItem("userType");
  const storedMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? storedMerchantId : reduxSelectedMerchant;

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getCustomerFeedback({
      ...filters,
      ...(selectedMerchant && selectedMerchant !== "all" ? { merchantId: selectedMerchant } : {}),
      page: pageNo,
      pageSize: limit,
    });
    setPage(data);
    // Rows that scrolled away must not stay selected — a bulk re-sync should only ever act
    // on what the operator can actually see.
    setSelected([]);
    setLoading(false);
  }, [filters, selectedMerchant, pageNo, limit]);

  useEffect(() => { load(); }, [load]);

  // Any filter change invalidates the current page number; staying on page 7 of a result
  // set that now has two pages shows an empty table.
  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPageNo(1);
  };

  // Memoized because `page.rows || []` would be a fresh array on every render, which makes
  // the useMemo below it re-run every time and defeats its own purpose.
  const rows = useMemo(() => page.rows || [], [page.rows]);

  // Only rows Odoo has not already accepted can be re-driven; re-pushing a synced one would
  // create a second review in Odoo for an order that already has ours.
  const retryableIds = useMemo(
    () => rows.filter((r) => r.pushStatus !== "success").map((r) => r.id),
    [rows]
  );

  const allSelected = retryableIds.length > 0 && selected.length === retryableIds.length;

  const toggleAll = () => setSelected(allSelected ? [] : retryableIds);

  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRetry = async (ids) => {
    if (!ids.length) return;
    setSyncing(true);
    const result = await retryCustomerFeedbackPush(ids, selectedMerchant);
    setSyncing(false);
    if (result) {
      successToast(result.message || `${result.updated} queued for the next sync.`);
      // Close the detail view before reloading: its `row` is a snapshot, so leaving it open
      // would show the pre-retry status next to a table that has already moved on.
      setDetail(null);
      load();
    }
  };

  const staleCount = rows.filter((r) => r.pushStatus === "stale").length;

  return (
    <Fragment>
      <Breadcrumb title="Customer Feedback" parent="Feedback" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
              <div>
                <h5 style={{ margin: 0, color: C.text, fontWeight: 700 }}>Customer Feedback</h5>
                <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>
                  Ratings left by scanning the QR on a receipt. They sync to Odoo automatically —
                  a <strong>stale</strong> entry means Odoo never received that order, so its review
                  could not be attached.
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button style={iconBtn} onClick={load} disabled={loading}>
                  <RefreshCw size={13} /> Refresh
                </button>
                <button
                  style={{ ...coralBtn, opacity: selected.length && !syncing ? 1 : 0.6, cursor: selected.length && !syncing ? "pointer" : "not-allowed" }}
                  disabled={!selected.length || syncing}
                  onClick={() => handleRetry(selected)}
                >
                  <RotateCw size={14} /> {syncing ? "Queuing…" : `Sync now${selected.length ? ` (${selected.length})` : ""}`}
                </button>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              <select style={field} value={filters.pushStatus} onChange={(e) => setFilter("pushStatus", e.target.value)}>
                <option value="">All sync statuses</option>
                <option value="stale">Stale — needs attention</option>
                <option value="pending">Waiting to sync</option>
                <option value="success">Synced</option>
                <option value="failed">Failed</option>
              </select>

              <select style={field} value={filters.rating} onChange={(e) => setFilter("rating", e.target.value)}>
                <option value="">All ratings</option>
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>{r} star{r > 1 ? "s" : ""}</option>
                ))}
              </select>

              <input
                style={{ ...field, width: 130 }}
                placeholder="Branch id"
                value={filters.branchId}
                onChange={(e) => setFilter("branchId", e.target.value)}
              />

              <input style={field} type="date" value={filters.from} onChange={(e) => setFilter("from", e.target.value)} />
              <input style={field} type="date" value={filters.to} onChange={(e) => setFilter("to", e.target.value)} />

              {Object.values(filters).some(Boolean) && (
                <button style={iconBtn} onClick={() => { setFilters(EMPTY_FILTERS); setPageNo(1); }}>
                  Clear
                </button>
              )}
            </div>

            {staleCount > 0 && filters.pushStatus !== "stale" && (
              <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: "#FEE4E2", color: "#B42318", fontSize: 12.5, fontWeight: 600 }}>
                {staleCount} {staleCount === 1 ? "entry on this page has" : "entries on this page have"} stopped
                retrying. Confirm the orders reached Odoo, then select them and press Sync now.
              </div>
            )}

            {loading ? (
              <RLoader />
            ) : rows.length === 0 ? (
              <div style={{ padding: "32px 0", textAlign: "center", fontSize: 13, color: C.textLight }}>
                {Object.values(filters).some(Boolean)
                  ? "No feedback matches these filters."
                  : "No feedback yet. It arrives when customers scan the QR on their receipt."}
              </div>
            ) : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        <th style={{ ...tbl.th, width: 36 }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            disabled={retryableIds.length === 0}
                            title="Select all re-syncable rows on this page"
                          />
                        </th>
                        <th style={tbl.th}>When</th>
                        <th style={tbl.th}>Rating</th>
                        <th style={tbl.th}>Comment</th>
                        <th style={tbl.th}>Photos</th>
                        <th style={tbl.th}>Customer</th>
                        <th style={tbl.th}>Branch</th>
                        <th style={tbl.th}>Order</th>
                        <th style={tbl.th}>Sync</th>
                        <th style={{ ...tbl.th, textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const meta = STATUS_META[row.pushStatus] || STATUS_META.pending;
                        const retryable = row.pushStatus !== "success";
                        return (
                          <tr
                            key={row.id}
                            onClick={() => setDetail(row)}
                            style={{ cursor: "pointer" }}
                            title="Open full feedback"
                          >
                            {/* The checkbox selects for bulk sync; it must not also open the
                                detail view, or ticking three rows opens three modals. */}
                            <td style={tbl.td} onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selected.includes(row.id)}
                                onChange={() => toggleOne(row.id)}
                                disabled={!retryable}
                              />
                            </td>
                            <td style={{ ...tbl.td, whiteSpace: "nowrap", color: C.textMed }}>
                              {new Date(row.createdAt).toLocaleString()}
                            </td>
                            <td style={tbl.td}>
                              <Stars value={row.rating} />
                            </td>
                            <td style={{ ...tbl.td, maxWidth: 280 }}>
                              {row.comment
                                ? <span title={row.comment}>{truncate(row.comment, 90)}</span>
                                : <span style={{ color: C.textLight }}>—</span>}
                            </td>
                            <td style={tbl.td}>
                              {row.images?.length ? (
                                <div style={{ display: "inline-flex", gap: 4 }}>
                                  {row.images.map((src) => (
                                    <img
                                      key={src}
                                      src={src}
                                      alt=""
                                      style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 6, border: `1px solid ${C.border}` }}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: C.textLight }}>—</span>
                              )}
                            </td>
                            <td style={tbl.td}>
                              {row.customerName || row.customerPhone ? (
                                <div>
                                  <div style={{ fontWeight: 600, color: C.text }}>{row.customerName || "—"}</div>
                                  <div style={{ fontSize: 11.5, color: C.textMed }}>
                                    {row.customerPhone || ""}
                                    {row.contactSource === "customer" && (
                                      <span style={{ marginLeft: 6, color: C.coral, fontWeight: 600 }}>· new</span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span style={{ color: C.textLight }}>Walk-in</span>
                              )}
                            </td>
                            <td style={tbl.td}>{row.merchantBranchId || <span style={{ color: C.textLight }}>—</span>}</td>
                            <td style={{ ...tbl.td, fontSize: 11.5, color: C.textMed }}>
                              <div title={row.orderUid} style={{ fontFamily: "monospace" }}>
                                {row.transactionNo || truncate(row.orderUid, 18)}
                              </div>
                            </td>
                            <td style={tbl.td}>
                              <Badge color={meta.color} bg={meta.bg}>{meta.label}</Badge>
                              {row.pushStatus !== "success" && row.pushAttempts > 0 && (
                                <div style={{ fontSize: 11, color: C.textLight, marginTop: 3 }}>
                                  attempt {row.pushAttempts} of {row.maxPushAttempts}
                                </div>
                              )}
                              {row.pushError && (
                                <div title={row.pushError} style={{ fontSize: 11, color: C.textLight, marginTop: 3, maxWidth: 200 }}>
                                  {truncate(row.pushError, 40)}
                                </div>
                              )}
                            </td>
                            <td style={{ ...tbl.td, textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                <button style={iconBtn} onClick={() => setDetail(row)}>
                                  <Eye size={13} /> View
                                </button>
                                {retryable && (
                                  <button style={iconBtn} disabled={syncing} onClick={() => handleRetry([row.id])}>
                                    <RotateCw size={13} /> Sync
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <TablePagination
                  total={page.total}
                  page={page.page}
                  limit={limit}
                  onPageChange={setPageNo}
                  onLimitChange={(l) => { setLimit(l); setPageNo(1); }}
                />
              </>
            )}
          </RCard>
        </div>
      </Container>

      <FeedbackDetailModal
        isOpen={!!detail}
        toggle={() => setDetail(null)}
        row={detail}
        onRetry={handleRetry}
        syncing={syncing}
      />
    </Fragment>
  );
};

const truncate = (text, max) => {
  const str = String(text ?? "");
  return str.length > max ? `${str.slice(0, max)}…` : str;
};

const Stars = ({ value }) => (
  <span style={{ display: "inline-flex", gap: 1, whiteSpace: "nowrap" }} title={`${value} of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={13}
        // A low rating is the one an operator should spot while scanning the column, so the
        // colour carries the signal rather than only the count of filled stars.
        color={n <= value ? (value <= 2 ? "#B42318" : C.coral) : C.border}
        fill={n <= value ? (value <= 2 ? "#B42318" : C.coral) : "none"}
      />
    ))}
  </span>
);

export default CustomerFeedbackList;
